import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Icons from 'lucide-react';
import { Input } from '@/components/ui/input';

// Curated lucide icons that make sense for business sectors
export const SECTOR_ICONS = [
  'Briefcase', 'Building2', 'Store', 'ShoppingCart', 'ShoppingBag', 'UtensilsCrossed', 'ChefHat', 'Coffee', 'Croissant', 'Wine',
  'BedDouble', 'Hotel', 'Plane', 'PlaneTakeoff', 'Ship', 'Anchor', 'Car', 'Bus', 'Truck', 'Bike',
  'HardHat', 'Hammer', 'Wrench', 'Drill', 'PaintRoller', 'Ruler', 'Factory', 'Cog', 'Zap', 'Droplets',
  'Sun', 'Leaf', 'Sprout', 'TreePalm', 'Fish', 'Tractor', 'Flower2', 'Dog',
  'Stethoscope', 'HeartPulse', 'Pill', 'Syringe', 'Microscope', 'Glasses', 'HeartHandshake', 'HandHeart', 'Baby', 'Accessibility',
  'GraduationCap', 'BookOpen', 'School', 'Pencil', 'Languages',
  'Laptop', 'Code2', 'Cpu', 'Smartphone', 'Wifi', 'Headset', 'Phone', 'Radio', 'Tv',
  'Megaphone', 'Camera', 'Video', 'Music', 'Palette', 'Brush', 'Film', 'Mic',
  'Landmark', 'Banknote', 'Coins', 'CreditCard', 'Calculator', 'PiggyBank', 'Receipt', 'Scale', 'Gavel', 'FileText',
  'Shield', 'ShieldCheck', 'Lock', 'Siren', 'Eye',
  'Sparkles', 'SprayCan', 'Shirt', 'Scissors', 'Gem', 'Crown',
  'PartyPopper', 'Gift', 'Cake', 'Ticket', 'Dumbbell', 'Trophy', 'Bike', 'Waves',
  'Package', 'Boxes', 'Warehouse', 'Container', 'Forklift', 'MapPin', 'Globe', 'Users', 'UserCheck', 'Handshake',
  'Home', 'Key', 'DoorOpen', 'Building', 'Newspaper', 'Mail', 'ClipboardList', 'Star',
].filter((v, i, a) => a.indexOf(v) === i && Icons[v]);

export const SectorIcon = ({ name, className = 'h-5 w-5' }) => {
  const Icon = Icons[name] || Icons.Briefcase;
  return <Icon className={className} aria-hidden="true" />;
};

export const IconPicker = ({ value, onChange }) => {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? SECTOR_ICONS.filter((n) => n.toLowerCase().includes(s)) : SECTOR_ICONS;
  }, [q]);

  return (
    <div data-testid="icon-picker">
      <div className="flex items-center gap-3 mb-2">
        <span className="h-10 w-10 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center shrink-0" data-testid="icon-picker-preview">
          <SectorIcon name={value} />
        </span>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('adminCategories.iconSearch')} data-testid="icon-picker-search" />
      </div>
      <div className="grid grid-cols-8 gap-1 max-h-44 overflow-y-auto rounded-md border border-gray-200 p-2 bg-white" role="listbox" aria-label={t('adminCategories.fieldIcon')}>
        {list.map((name) => (
          <button
            key={name}
            type="button"
            title={name}
            role="option"
            aria-selected={value === name}
            onClick={() => onChange(name)}
            className={`h-9 w-9 rounded-md flex items-center justify-center transition-colors ${value === name ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-blue-50 hover:text-blue-700'}`}
            data-testid={`icon-option-${name}`}
          >
            <SectorIcon name={name} className="h-4 w-4" />
          </button>
        ))}
        {list.length === 0 && <p className="col-span-8 text-xs text-gray-500 p-2">{t('adminCategories.iconNone')}</p>}
      </div>
      <p className="text-[11px] text-gray-500 mt-1">{t('adminCategories.iconSelected')}: <code className="bg-gray-100 px-1 rounded">{value || 'Briefcase'}</code></p>
    </div>
  );
};
